import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Image,
  List,
  Message,
  Modal,
  Segment,
} from 'semantic-ui-react';
import { isEmpty, get } from 'lodash';
import { format } from 'date-fns';
import {
  doApproveNpwp,
  doRejectNpwp,
  doSetNpwpStatusPending,
  setNpwpApproveModalShown,
  setNpwpDetailModalShown,
  setNpwpEditModalShown,
  setNpwpRejectModalShown,
  setNpwpReviewDetailsModalShown,
  submitEditNpwp,
} from 'state/npwp/actions';
import ApprovalStatusMessage from 'pages/Npwp/ApprovalStatusMessage';
import NpwpImagePreviewModal from 'pages/Npwp/NpwpImagePreviewModal';
import NpwpApproveConfirmationModal from 'pages/Npwp/NpwpApproveConfirmationModal';
import NpwpRejectConfirmationModal from 'pages/Npwp/NpwpRejectConfirmationModal';
import NpwpReviewDetailsModal from 'pages/Npwp/NpwpReviewDetailsModal';
import EditNpwpModal from 'pages/Npwp/EditNpwpModal';

function BusinessDetailModal() {
  const dispatch = useDispatch();

  const {
    modalShown,
    errorMessages,
    business,
    loadingStates,
    npwp,
    approvalLogs,
    approveModal,
    rejectModal,
    reviewDetailsModal,
    editNpwpModal,
  } = useSelector(state => state.npwp.businessDetail);

  const getBusinessNameOrOwnerName = () => {
    if (!business) {
      return '';
    }
    return (business.name || '').trim() !== ''
      ? business.name
      : business.owner_name;
  };

  const onClose = () => dispatch(setNpwpDetailModalShown(false));

  const renderBusinessDetail = () => {
    return (
      <Segment loading={loadingStates.business}>
        {!!business && (
          <List>
            <List.Item>
              <strong>Business ID:</strong> {business.id}
            </List.Item>
            <List.Item>
              <strong>Business Name:</strong> {business.name}
            </List.Item>
            <List.Item>
              <strong>Owner Name:</strong> {business.owner_name}
            </List.Item>
            <List.Item>
              <strong>Business Address:</strong> {business.address}
            </List.Item>
            <List.Item>
              <strong>Registered Merchant Email:</strong> {business.owner_email}
            </List.Item>
            <List.Item>
              <strong>Registered Phone Number:</strong> {business.phone}
            </List.Item>
            <List.Item>
              <strong>Registered Date:</strong>{' '}
              {format(new Date(business.registered_date), 'dd-MM-yyyy')}
            </List.Item>
            <List.Item>
              <strong>Expired At:</strong>{' '}
              {format(new Date(business.expired_at), 'dd-MM-yyyy')}
            </List.Item>
          </List>
        )}
      </Segment>
    );
  };

  const [npwpImagePreviewModalShown, setNpwpImagePreviewModalShown] = useState(
    false
  );

  const onEditNpwpButtonClicked = () => dispatch(setNpwpEditModalShown(true));
  const onEditNpwpModalClosed = () => dispatch(setNpwpEditModalShown(false));
  const oneEditNpwpConfirmed = form =>
    dispatch(submitEditNpwp(business.id, form));

  const renderNpwpDetails = () => {
    return (
      <Segment loading={loadingStates.npwp}>
        <h3>NPWP Details</h3>
        {!!npwp && (
          <>
            <List>
              <List.Item>
                <strong>NPWP Number:</strong> {npwp.number}
              </List.Item>
              <List.Item>
                <strong>NPWP Name:</strong> {npwp.name}
              </List.Item>
            </List>
            {!!npwp.image && (
              <>
                <Image
                  onClick={() => setNpwpImagePreviewModalShown(true)}
                  src={npwp.image}
                  size="small"
                  style={{ cursor: 'pointer' }}
                />
                <small>
                  <em>Click image to enlarge</em>
                </small>
              </>
            )}
          </>
        )}
        <br />
        {['approved', 'rejected'].includes(
          get(business, 'npwp_status', '')
        ) && (
          <Button primary onClick={onEditNpwpButtonClicked}>
            Edit NPWP Details
          </Button>
        )}
      </Segment>
    );
  };

  const renderModalContent = () => {
    return (
      <>
        {renderBusinessDetail()}
        {renderNpwpDetails()}
        <Segment loading={loadingStates.log}>
          {!!business && (
            <ApprovalStatusMessage
              currentStatus={business.npwp_status}
              approvalLogs={approvalLogs}
            />
          )}
        </Segment>
      </>
    );
  };

  const onApproveButtonClicked = () => dispatch(setNpwpApproveModalShown(true));
  const onApproveConfirmModalClosed = () =>
    dispatch(setNpwpApproveModalShown(false));
  const onApproveConfirmModalConfirmed = () =>
    dispatch(doApproveNpwp(business.id));

  const onRejectButtonClicked = () => dispatch(setNpwpRejectModalShown(true));
  const onRejectConfirmModalClosed = () =>
    dispatch(setNpwpRejectModalShown(false));
  const onRejectConfirmModalConfirmed = reason =>
    dispatch(doRejectNpwp(business.id, reason));

  const onUpdateButtonClicked = () =>
    dispatch(setNpwpReviewDetailsModalShown(true));
  const onReviewModalClosed = () =>
    dispatch(setNpwpReviewDetailsModalShown(false));
  const onReviewModalConfirmed = () =>
    dispatch(doSetNpwpStatusPending(business.id));

  return (
    <>
      {!!business && (
        <>
          {!!npwp && (
            <NpwpImagePreviewModal
              onClose={() => setNpwpImagePreviewModalShown(false)}
              npwp={npwp}
              open={npwpImagePreviewModalShown}
            />
          )}
          <NpwpApproveConfirmationModal
            onClose={onApproveConfirmModalClosed}
            onConfirm={onApproveConfirmModalConfirmed}
            isLoading={approveModal.isLoading}
            businessOrOwnerName={getBusinessNameOrOwnerName()}
            shown={approveModal.shown}
          />
          <NpwpRejectConfirmationModal
            onClose={onRejectConfirmModalClosed}
            onConfirm={onRejectConfirmModalConfirmed}
            isLoading={rejectModal.isLoading}
            shown={rejectModal.shown}
          />
          <NpwpReviewDetailsModal
            onConfirm={onReviewModalConfirmed}
            isLoading={reviewDetailsModal.isLoading}
            onClose={onReviewModalClosed}
            open={reviewDetailsModal.shown}
          />
          <EditNpwpModal
            onClose={onEditNpwpModalClosed}
            onSubmit={oneEditNpwpConfirmed}
            isLoading={editNpwpModal.isLoading}
            shown={editNpwpModal.shown}
          />
        </>
      )}
      <Modal open={modalShown} onClose={onClose}>
        <Modal.Header>Business NPWP Details</Modal.Header>
        <Modal.Content>
          <Message negative hidden={isEmpty(errorMessages)}>
            <Message.Header>Error</Message.Header>
            <Message.List>
              {errorMessages.map(error => (
                <Message.Item>{error}</Message.Item>
              ))}
            </Message.List>
          </Message>
          {renderModalContent()}
        </Modal.Content>
        <Modal.Actions>
          {get(business, 'npwp_status', '') === 'pending' && (
            <>
              <Button positive onClick={onApproveButtonClicked}>
                Approve
              </Button>
              <Button negative onClick={onRejectButtonClicked}>
                Reject
              </Button>
            </>
          )}
          {get(business, 'npwp_status', '') === 'rejected' && (
            <Button primary onClick={onUpdateButtonClicked}>
              Update
            </Button>
          )}
          <Button onClick={onClose}>Close</Button>
        </Modal.Actions>
      </Modal>
    </>
  );
}

export default BusinessDetailModal;
